import { Box, Button, Image } from '@astro-ui/components'
import theme from '@astro-ui/theme'
import { ChevronLeft, ChevronRight, Close } from '@astronautsid/wpe-icons'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import { useOnCreate, useOnDestroy } from 'utils/customHooks/useLifeCycleHooksAndRouter'

type ImagePreviewOverlayPropType = {
  className?: string
  images: string[]
  thumbnails?: string[]
  currentIndex: number
  onClose: () => void
  onSetCurrentIndex: (index: number) => void
}

const FadeInAnimation = css`
  animation: fadeIn 0.3s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: scale(0.97);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

const FadeOutAnimation = css`
  animation: fadeOut 0.3s ease-in-out;
  @keyframes fadeOut {
    from {
      opacity: 1;
      transform: scale(1);
    }
    to {
      opacity: 0;
      transform: scale(0.97);
    }
  }
`

const ImagePreviewOverlay = ({
  className = '',
  images,
  thumbnails = [],
  currentIndex,
  onClose,
  onSetCurrentIndex,
}: ImagePreviewOverlayPropType) => {
  const [Animation, setAnimation] = useState(FadeInAnimation)
  const currentImage = images[currentIndex] || ''
  const shouldShowThumbnails = thumbnails.length > 1 && thumbnails.length === images.length

  useOnCreate(() => {
    window.addEventListener('keydown', handleKeyDown)
  }, [])

  useOnDestroy(() => {
    window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') handleClose()
    if (e.key === 'ArrowLeft') handlePrev()
    if (e.key === 'ArrowRight') handleNext()
  }

  const handlePrev = () => {
    onSetCurrentIndex((currentIndex - 1 + images.length) % images.length)
  }

  const handleNext = () => {
    onSetCurrentIndex((currentIndex + 1) % images.length)
  }

  const handleClose = () => {
    setAnimation(FadeOutAnimation)
    setTimeout(() => {
      onClose()
    }, 300)
  }

  return (
    <ImagePreviewWrapper
      animation={Animation}
      id="image-preview"
      className={`image-preview-overlay ${className}`}
    >
      <Box position="absolute" top={14} right={14}>
        <ButtonnAction onClick={handleClose}>
          <Close type="outline" color={theme.palette.background.paper} size={30} />
        </ButtonnAction>
      </Box>

      <Box height={shouldShowThumbnails ? '89vh' : '100vh'} width="100vw" display="flex">
        <Box
          width="100px"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
        >
          {images.length > 1 && (
            <ButtonnAction onClick={handlePrev}>
              <ChevronLeft type="outline" color={theme.palette.background.paper} size={50} />
            </ButtonnAction>
          )}
        </Box>
        <Box flex={1} padding="24px" display="flex" alignItems="center" justifyContent="center">
          <Box height="100%" width="95%" display="flex" alignItems="center" justifyContent="center">
            <Image
              src={currentImage}
              alt="Preview Image"
              objectFit="contain"
              maxWidth="100%"
              maxHeight="100%"
            />
          </Box>
        </Box>
        <Box width="100px" height="100%" display="flex" alignItems="center">
          {images.length > 1 && (
            <ButtonnAction onClick={handleNext}>
              <ChevronRight type="outline" color={theme.palette.background.paper} size={50} />
            </ButtonnAction>
          )}
        </Box>
      </Box>

      {shouldShowThumbnails && (
        <Box height="10vh" position="absolute" bottom={0} width="100vw">
          <Box height="100%" display="flex" alignItems="center" justifyContent="center" gap="10px">
            {thumbnails.map((thumb, index) => (
              <Box
                key={`thumb-${index + 1}`}
                height={currentIndex === index ? '92%' : '70%'}
                opacity={currentIndex === index ? 1 : 0.5}
                width="80px"
                overflow="hidden"
                borderRadius={10}
                style={{ cursor: 'pointer' }}
                onClick={() => onSetCurrentIndex(index)}
              >
                <Image
                  src={thumb}
                  alt="Thumbnail Image"
                  objectFit="contain"
                  width="100%"
                  height="100%"
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </ImagePreviewWrapper>
  )
}

export default ImagePreviewOverlay

const ImagePreviewWrapper = styled(Box)<{ animation: ReturnType<typeof css> }>`
  position: fixed;
  inset: 0;
  z-index: ${({ theme: { zIndex } }) => zIndex.modal + 10};
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  ${({ animation }) => animation}
`

const ButtonnAction = styled(Button).attrs({ variant: 'text' })`
  min-width: unset;
`
